import { supabase } from '../lib/supabase'
import { sanitizeText } from '../utils/sanitizer'
import Sentry from '../lib/sentry'
import { checkLimit } from './usageService'

// Create project
export const createProject = async (projectData) => {
  try {
    // Check if organization has reached project limit
    const limitCheck = await checkLimit(projectData.organization_id, 'projects')
    if (!limitCheck.allowed) {
      const error = new Error(limitCheck.message)
      error.code = 'LIMIT_REACHED'
      error.limitType = 'projects'
      throw error
    }

    // Sanitize inputs
    const cleanData = {
      ...projectData,
      title: sanitizeText(projectData.title),
      description: projectData.description ? sanitizeText(projectData.description) : null,
    }

    const { data, error } = await supabase
      .from('projects')
      .insert([cleanData])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    Sentry.captureException(error, {
      context: { function: 'createProject' }
    })
    throw error
  }
}

// Get projects by organization with pagination
export const getProjectsByOrganization = async (organizationId, offset = 0, limit = 20) => {
  try {
    // Get total count
    const { count } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId)

    // Get page data
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        versions:project_versions(count)
      `)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (error) throw error

    return {
      data: data.map(p => ({
        ...p,
        version_count: p.versions[0]?.count || 0
      })),
      total: count
    }
  } catch (error) {
    Sentry.captureException(error, {
      context: { function: 'getProjectsByOrganization', organizationId }
    })
    throw error
  }
}

// Get single project
export const getProject = async (projectId) => {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      *,
      organization:organizations(*),
      versions:project_versions(
        *,
        comments:comments(
          *,
          user:users(email, full_name)
        )
      )
    `)
    .eq('id', projectId)
    .single()

  if (error) throw error
  return data
}

// Update project
export const updateProject = async (projectId, updates) => {
  const { data, error } = await supabase
    .from('projects')
    .update(updates)
    .eq('id', projectId)
    .select()
    .single()

  if (error) throw error
  return data
}

// Delete project
export const deleteProject = async (projectId) => {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId)

  if (error) throw error
}

// Generate client access token with cryptographic security
export const generateClientToken = async (projectId) => {
  // Generate cryptographically secure token (64 chars hex)
  const randomBytes = new Uint8Array(32)
  crypto.getRandomValues(randomBytes)
  const token = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('')
  
  // Create hash for database storage (we store hash, return plaintext)
  const encoder = new TextEncoder()
  const data = encoder.encode(token)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const tokenHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  
  const { data: project, error } = await supabase
    .from('projects')
    .update({ 
      client_token: tokenHash, // Store hash, not plaintext
      token_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days (shorter)
    })
    .eq('id', projectId)
    .select()
    .single()

  if (error) throw error
  
  // Return plaintext token only once (user must save it)
  return token
}

// Get project by client token
export const getProjectByToken = async (token) => {
  // Hash the provided token to compare with stored hash
  const encoder = new TextEncoder()
  const data = encoder.encode(token)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const tokenHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  
  const { data: project, error } = await supabase
    .from('projects')
    .select(`
      *,
      versions:project_versions(
        *,
        comments:comments(
          *,
          user:users(email, full_name)
        )
      )
    `)
    .eq('client_token', tokenHash)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('Invalid or expired access token')
    }
    throw error
  }

  // Check if token is expired (secondary check, primary is in RLS)
  if (project.token_expires_at && new Date(project.token_expires_at) < new Date()) {
    throw new Error('Access token has expired')
  }

  return project
}
